import { registrarEvento } from '../medicion';
import { useEffect, useRef, useState } from 'react';
import type { CSSProperties } from 'react';

/*
  Chat de TicoBaterín (esquina inferior IZQUIERDA).
  El botón de WhatsApp del sitio (WhatsAppFab) sigue en la derecha.

  - Habla con el backend del sitio: POST /api/chat/  (puente hacia el servidor Ollama)
  - Los precios vienen del campo "cotizacion" (sale de la base de datos, no del modelo).
  - El mensaje de WhatsApp se arma con la cotización solo cuando hay UN vehículo definido.
*/

const API_CHAT = '/api/chat/';
const API_INFO = '/api/informacion/';
const WHATSAPP_RESPALDO = '50685076464';   // si no se puede leer de Información
const NOMBRE = 'TicoBaterín';
const NEGOCIO = 'TicoBaterías';
const AVATAR = '/ticobaterin-sticker.webp';   // en frontend/publico/public/ (sticker sin fondo)
const PROPORCION = 301 / 320;                // ancho / alto del sticker

// Colores del widget (ajustar a los del sitio si hace falta)
const COLOR = '#0b3d91';
const COLOR_OSCURO = '#082d6b';

type Opcion = { producto_id: number; nombre: string; marca: string; precio: number | null };
type Cotizacion = { vehiculo: string; bateria: string; opciones: Opcion[] };
type Mensaje = { rol: 'user' | 'bot'; texto: string; ofrecerWhatsapp?: boolean };

const SALUDO: Mensaje = {
  rol: 'bot',
  texto: `¡Hola! Soy ${NOMBRE} 🔋\nTe ayudo a encontrar la batería de tu vehículo, con precio al instante.\n\n¿Qué carro tienes? (marca, modelo y año)`,
};

const nuevaSesion = () => 'web-' + Date.now() + '-' + Math.random().toString(36).slice(2, 9);
const colones = (v: number) => '₡' + String(Math.round(v)).replace(/\B(?=(\d{3})+(?!\d))/g, ' ');

function leerSesion(): string {
  try {
    const s = sessionStorage.getItem('tb_session_id');
    if (s) return s;
    const n = nuevaSesion();
    sessionStorage.setItem('tb_session_id', n);
    return n;
  } catch {
    return nuevaSesion();
  }
}

function Sticker({ alto, sombra }: { alto: number; sombra?: boolean }) {
  const [fallo, setFallo] = useState(false);
  if (fallo) {
    return (
      <span className="rounded-full flex items-center justify-center shrink-0 text-white"
        style={{ width: alto, height: alto, fontSize: alto * 0.5, background: COLOR }}>
        🔋
      </span>
    );
  }
  return (
    <img src={AVATAR} alt={NOMBRE} onError={() => setFallo(true)} draggable={false}
      className="shrink-0 select-none"
      style={{
        height: alto,
        width: Math.round(alto * PROPORCION),
        filter: sombra ? 'drop-shadow(0 6px 8px rgba(0,0,0,0.28))' : undefined,
      }} />
  );
}

// Animacion de saludo cada 6 s (se desactiva si el usuario pide menos movimiento)
const ESTILOS_ANIMACION = `
@keyframes tb-saludo {
  0%, 84%, 100% { transform: rotate(0deg); }
  87% { transform: rotate(-7deg); }
  90% { transform: rotate(6deg); }
  93% { transform: rotate(-4deg); }
  96% { transform: rotate(2deg); }
}
.tb-mascota { animation: tb-saludo 6s ease-in-out infinite; transform-origin: 50% 100%; }
.tb-mascota:hover { animation-play-state: paused; }
@media (prefers-reduced-motion: reduce) { .tb-mascota { animation: none; } }
`;

export default function ChatTicoBaterin() {
  const [abierto, setAbierto] = useState(false);
  const [nube, setNube] = useState(false);
  const [mensajes, setMensajes] = useState<Mensaje[]>([SALUDO]);
  const [texto, setTexto] = useState('');
  const [cargando, setCargando] = useState(false);
  const [frase, setFrase] = useState('Escribiendo...');
  const [sesion, setSesion] = useState<string>(leerSesion);
  const [cotizacion, setCotizacion] = useState<Cotizacion | null>(null);
  const [sinCompat, setSinCompat] = useState(false);
  const [whatsapp, setWhatsapp] = useState(WHATSAPP_RESPALDO);
  const [esMovil, setEsMovil] = useState(false);
  const [vista, setVista] = useState({ alto: 0, teclado: 0 });
  const finRef = useRef<HTMLDivElement>(null);

  // Mide la pantalla VISIBLE (en móvil el teclado la achica) para que el chat se acomode
  useEffect(() => {
    const vv = window.visualViewport;
    const medir = () => {
      setEsMovil(window.innerWidth < 640);
      const alto = vv ? vv.height : window.innerHeight;
      const teclado = vv ? Math.max(0, window.innerHeight - vv.height - vv.offsetTop) : 0;
      setVista({ alto, teclado });
    };
    medir();
    window.addEventListener('resize', medir);
    vv?.addEventListener('resize', medir);
    vv?.addEventListener('scroll', medir);
    return () => {
      window.removeEventListener('resize', medir);
      vv?.removeEventListener('resize', medir);
      vv?.removeEventListener('scroll', medir);
    };
  }, []);

  // Número de WhatsApp desde Información (así se actualiza solo desde el admin)
  useEffect(() => {
    fetch(API_INFO)
      .then(r => r.json())
      .then(d => {
        const n = String(d?.whatsapp || '').replace(/\D/g, '');
        if (n.length >= 8) setWhatsapp(n);
      })
      .catch(() => {});
  }, []);

  // Nube de saludo: aparece a los 3 s, salvo que el visitante ya la haya cerrado
  useEffect(() => {
    let cerrada = false;
    try { cerrada = sessionStorage.getItem('tb_nube_cerrada') === '1'; } catch {}
    if (cerrada) return;
    const t = setTimeout(() => setNube(true), 3000);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    finRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [mensajes, cargando, abierto]);

  const cerrarNube = () => {
    setNube(false);
    try { sessionStorage.setItem('tb_nube_cerrada', '1'); } catch {}
  };

  const abrir = () => {
    setAbierto(true);
    cerrarNube();
  };

  const armarMensajeWhatsApp = () => {
    const delCliente = mensajes.filter(m => m.rol === 'user').slice(-3).map(m => '- ' + m.texto);
    let msg = `Hola ${NEGOCIO}, vengo del chat de la página web. `;

    if (cotizacion) {
      msg += `Quiero la batería para mi ${cotizacion.vehiculo} (batería ${cotizacion.bateria}).`;
      const ops = cotizacion.opciones.filter(o => o.precio).slice(0, 3);
      if (ops.length) {
        msg += '\nOpciones que me indicaron:\n' + ops.map(o => `- ${o.nombre}: ${colones(o.precio as number)}`).join('\n');
      }
    } else if (sinCompat) {
      msg += 'Necesito confirmar qué batería le sirve a mi vehículo:\n' + delCliente.join('\n');
    } else if (delCliente.length) {
      msg += 'Esto pregunté:\n' + delCliente.join('\n');
    } else {
      msg += 'Quiero información sobre baterías.';
    }

    // Links muy largos rompen la apertura de WhatsApp en Android (lección de la bitácora)
    if (msg.length > 600) msg = msg.slice(0, 600) + '...';
    return msg;
  };

  const irWhatsApp = () => {
    const url = `https://wa.me/${whatsapp}?text=${encodeURIComponent(armarMensajeWhatsApp())}`;
    // location.href es más confiable que window.open() para abrir la app en móvil
    window.location.href = url;

    // Reiniciar el chat para el siguiente cliente
    setTimeout(() => {
      const n = nuevaSesion();
      try { sessionStorage.setItem('tb_session_id', n); } catch {}
      setSesion(n);
      setMensajes([{ rol: 'bot', texto: '¡Hola de nuevo! ¿En qué más te puedo ayudar? 🔋' }]);
      setCotizacion(null);
      setSinCompat(false);
    }, 800);
  };

  const enviar = async () => {
    const t = texto.trim();
    if (!t || cargando) return;

    setMensajes(m => [...m, { rol: 'user', texto: t }]);
    setTexto('');
    setCargando(true);
    setFrase('Escribiendo...');

    // El modelo corre en CPU: mensajes de espera escalonados para que no parezca trabado
    const t1 = setTimeout(() => setFrase('Buscando la batería correcta...'), 7000);
    const t2 = setTimeout(() => setFrase('Un momento más, ya casi...'), 18000);
    const ctrl = new AbortController();
    const t3 = setTimeout(() => ctrl.abort(), 75000);

    try {
      const r = await fetch(API_CHAT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ session_id: sesion, mensaje: t }),
        signal: ctrl.signal,
      });
      const d = await r.json();

      // Cotización: se guarda solo si hay UN vehículo; si hay varios, aún no se sabe cuál es
      if (Array.isArray(d.cotizacion)) {
        if (d.cotizacion.length > 0) {
          const vehiculos = d.cotizacion.map((c: any) => c.vehiculo).join(' | ');
          registrarEvento('cotizacion', { vehiculo: vehiculos }, 'cotizacion:' + vehiculos);
        }
        setCotizacion(d.cotizacion.length === 1 ? d.cotizacion[0] : null);
      }
      if (d.sin_compatibilidad) setSinCompat(true);

      setMensajes(m => [...m, {
        rol: 'bot',
        texto: d.respuesta || 'Disculpa, no pude responder. ¿Me escribes por WhatsApp?',
        ofrecerWhatsapp: !!d.ofrecer_whatsapp,
      }]);
    } catch {
      setMensajes(m => [...m, {
        rol: 'bot',
        texto: 'Disculpa, tuve un problema técnico 😅 Escríbenos directo por WhatsApp y con gusto te ayudamos.',
        ofrecerWhatsapp: true,
      }]);
    } finally {
      clearTimeout(t1);
      clearTimeout(t2);
      clearTimeout(t3);
      setCargando(false);
    }
  };

  const ultimoIndiceBot = mensajes.map(m => m.rol).lastIndexOf('bot');

  // Por encima de cualquier otro botón flotante del sitio (incluido el de WhatsApp)
  const Z_CHAT = 2147483000;

  // Móvil: panel inferior del 62% de la pantalla; con el teclado abierto usa el espacio sobre él.
  // Escritorio: ventana flotante de 380 x 560.
  const estiloVentana: CSSProperties = esMovil
    ? {
        left: 8,
        right: 8,
        bottom: 8 + vista.teclado,
        height: vista.teclado > 0
          ? Math.max(260, vista.alto - 16)
          : Math.max(360, Math.round(vista.alto * 0.62)),
        zIndex: Z_CHAT,
      }
    : {
        left: 16,
        bottom: 16,
        width: 'min(380px, calc(100vw - 32px))',
        height: 'min(560px, calc(100vh - 32px))',
        zIndex: Z_CHAT,
      };

  const altoMascota = esMovil ? 90 : 112;                       // TicoBaterin de pie
  const anchoMascota = Math.round(altoMascota * PROPORCION);

  return (
    <>
      <style>{ESTILOS_ANIMACION}</style>
      {/* ---------- Nube de saludo ---------- */}
      {nube && !abierto && (
        <div className="fixed z-50" style={{ left: 16 + anchoMascota + 10, bottom: 16 + altoMascota * 0.45, maxWidth: esMovil ? 190 : 240 }}>
          <div className="relative bg-white rounded-2xl shadow-xl border border-gray-100 px-4 py-3 text-sm text-gray-700">
            <button
              onClick={cerrarNube}
              aria-label="Cerrar saludo"
              className="absolute -top-2 -right-2 w-6 h-6 rounded-full bg-gray-200 text-gray-600 text-xs leading-6 text-center hover:bg-gray-300"
            >
              ✕
            </button>
            <button onClick={abrir} className="text-left">
              ¡Hola! Soy <b>{NOMBRE}</b> 👋<br />¿Te cotizo la batería de tu carro?
            </button>
            <div className="absolute w-3 h-3 bg-white border-l border-b border-gray-100 rotate-45" style={{ left: -7, bottom: 16 }} />
          </div>
        </div>
      )}

      {/* ---------- Botón flotante (izquierda) ---------- */}
      {!abierto && (
        <button
          onClick={abrir}
          aria-label={`Abrir chat con ${NOMBRE}`}
          className="fixed z-50 tb-mascota bg-transparent border-0 p-0 transition-transform hover:scale-105"
          style={{ left: 16, bottom: 12 }}
        >
          <Sticker alto={altoMascota} sombra />
        </button>
      )}

      {/* ---------- Ventana del chat ---------- */}
      {abierto && (
        <div
          className="fixed bg-white rounded-2xl shadow-2xl border border-gray-100 flex flex-col overflow-hidden"
          style={estiloVentana}
        >
          {/* Encabezado */}
          <div className={`flex items-center gap-3 px-4 text-white ${esMovil ? 'py-2' : 'py-3'}`} style={{ background: COLOR }}>
            <Sticker alto={esMovil ? 40 : 46} />
            <div className="flex-1 min-w-0">
              <p className="font-bold leading-tight">{NOMBRE}</p>
              <p className="text-xs opacity-80 truncate">Asistente de {NEGOCIO}{esMovil ? '' : ' · en línea'}</p>
            </div>
            <button
              onClick={irWhatsApp}
              title="Hablar con un asesor por WhatsApp"
              aria-label="Hablar por WhatsApp"
              className="w-9 h-9 rounded-full bg-green-500 hover:bg-green-600 flex items-center justify-center"
            >
              <svg viewBox="0 0 24 24" width="18" height="18" fill="white" aria-hidden="true">
                <path d="M12 2a10 10 0 0 0-8.6 15.1L2 22l5-1.3A10 10 0 1 0 12 2zm0 18.2a8.2 8.2 0 0 1-4.2-1.2l-.3-.2-3 .8.8-2.9-.2-.3A8.2 8.2 0 1 1 12 20.2zm4.5-6.1c-.2-.1-1.5-.7-1.7-.8-.2-.1-.4-.1-.6.1l-.8 1c-.1.2-.3.2-.5.1a6.7 6.7 0 0 1-3.3-2.9c-.2-.4.2-.4.7-1.3.1-.2 0-.3 0-.4l-.8-1.8c-.2-.5-.4-.4-.6-.4h-.5c-.2 0-.4.1-.6.3a2.6 2.6 0 0 0-.8 1.9 4.5 4.5 0 0 0 1 2.4 10.2 10.2 0 0 0 3.9 3.4c1.5.6 2 .7 2.7.6.4-.1 1.5-.6 1.7-1.2.2-.6.2-1.1.1-1.2l-.4-.2z" />
              </svg>
            </button>
            <button
              onClick={() => setAbierto(false)}
              aria-label="Cerrar chat"
              className="w-9 h-9 rounded-full hover:bg-white/20 flex items-center justify-center text-lg"
            >
              ✕
            </button>
          </div>

          {/* Mensajes */}
          <div className="flex-1 overflow-y-auto px-4 py-4 space-y-3 bg-gray-50">
            {mensajes.map((m, i) => (
              <div key={i} className={m.rol === 'user' ? 'flex justify-end' : 'flex justify-start'}>
                <div className="max-w-[85%]">
                  <div
                    className={`px-4 py-2.5 rounded-2xl text-sm whitespace-pre-line break-words ${
                      m.rol === 'user' ? 'text-white rounded-br-md' : 'bg-white text-gray-800 border border-gray-100 rounded-bl-md'
                    }`}
                    style={m.rol === 'user' ? { background: COLOR } : undefined}
                  >
                    {m.texto}
                  </div>

                  {/* Botón de WhatsApp dentro del chat cuando el sistema lo ofrece */}
                  {m.rol === 'bot' && m.ofrecerWhatsapp && i === ultimoIndiceBot && (
                    <button
                      onClick={irWhatsApp}
                      className="mt-2 w-full bg-green-500 hover:bg-green-600 text-white text-sm font-bold py-2 rounded-xl"
                    >
                      📲 Sí, enviar a WhatsApp
                    </button>
                  )}

                  {/* Atajo en el saludo inicial para quien prefiere un humano */}
                  {i === 0 && mensajes.length === 1 && (
                    <button onClick={irWhatsApp} className="mt-2 text-xs text-gray-500 underline hover:text-gray-700">
                      Prefiero hablar con un asesor
                    </button>
                  )}
                </div>
              </div>
            ))}

            {cargando && (
              <div className="flex justify-start">
                <div className="bg-white border border-gray-100 px-4 py-2.5 rounded-2xl rounded-bl-md text-sm text-gray-500 italic">
                  {frase}
                </div>
              </div>
            )}
            <div ref={finRef} />
          </div>

          {/* Enviar cotización (visible cuando ya hay un vehículo cotizado) */}
          {cotizacion && !cargando && (
            <button
              onClick={irWhatsApp}
              className="mx-3 mt-2 bg-green-50 border border-green-200 text-green-700 text-xs font-bold py-2 rounded-xl hover:bg-green-100"
            >
              📲 Enviar esta cotización por WhatsApp
            </button>
          )}

          {/* Entrada */}
          <div className="flex items-center gap-2 p-3 border-t border-gray-100">
            <input
              value={texto}
              onChange={e => setTexto(e.target.value.slice(0, 500))}
              onKeyDown={e => { if (e.key === 'Enter') enviar(); }}
              placeholder="Ej: Toyota Corolla 2015"
              disabled={cargando}
              className="flex-1 border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none disabled:bg-gray-50"
              style={{ fontSize: 16 /* evita el zoom automático en iPhone */ }}
            />
            <button
              onClick={enviar}
              disabled={cargando || !texto.trim()}
              aria-label="Enviar mensaje"
              className="w-11 h-11 rounded-xl text-white flex items-center justify-center disabled:opacity-40"
              style={{ background: COLOR_OSCURO }}
            >
              ➤
            </button>
          </div>
        </div>
      )}
    </>
  );
}
